import { Component, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { UserPreferencesService, PinnedScanner } from '../../@services/user-preferences.service';
import { ToastService } from '../../@services/toast.service';

// /admin/pinned — manage the user's pinned scanners.
//
// Draft/live split: `draftPins` is the working copy the operator edits;
// only Save PUTs to the backend. Revert restores from the last-loaded
// server snapshot. Follows the fleet forms rule (Save/Revert/Cancel; no
// autosave; unsaved-close confirms).
//
// Suggested pins: for a first-time user, seed the UI with a
// one-click chip for Internal Tracker (Mike's stated first pin). It
// doesn't touch the backend until the operator clicks it.
@Component({
  selector: 'app-manage-pins',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './manage-pins.component.html',
  host: { class: 'block h-full' },
})
export class ManagePinsComponent implements OnInit {
  draftPins = signal<PinnedScanner[]>([]);
  saving = signal(false);

  // Add-form state — kept separate from the drafts so the operator can
  // type an entry, click Add, and see it appended without clobbering
  // fields half-way through.
  newLabel = '';
  newKey = '';
  newUrl = '';

  // Slug matches the F2 tracker's scanner entry (Scanners.Id='f2-tracker')
  // so pinning here + pinning the scanner-row's thumbtack land on the same
  // sidebar row instead of two duplicate entries.
  readonly SUGGESTED: PinnedScanner[] = [
    {
      key: 'f2-tracker',
      label: 'Internal Tracker',
      url: 'https://f2-pmo-srvr.f2-tech.ai/f2-tracker/',
    },
  ];

  constructor(
    private prefs: UserPreferencesService,
    private toast: ToastService,
  ) {}

  readonly dirty = computed(() => {
    const server = this.prefs.pinnedScanners();
    const draft = this.draftPins();
    if (server.length !== draft.length) return true;
    return draft.some((p, i) => {
      const s = server[i];
      return !s || s.key !== p.key || s.label !== p.label || s.url !== p.url;
    });
  });

  async ngOnInit() {
    await this.prefs.loadOnce();
    this._reset();
  }

  /** Suggested-pin chip: append (unless already present) and set dirty. */
  addSuggested(pin: PinnedScanner) {
    const existing = this.draftPins().find(p => p.key === pin.key);
    if (existing) {
      this.toast.error('Already pinned', `${pin.label} is already in your list.`);
      return;
    }
    this.draftPins.update((cur) => [...cur, { ...pin }]);
  }

  isSuggestedAlreadyPinned(pin: PinnedScanner): boolean {
    return this.draftPins().some(p => p.key === pin.key);
  }

  addNew() {
    const label = this.newLabel.trim();
    const rawKey = (this.newKey.trim() || this._deriveKey(label)).toLowerCase();
    const url = this.newUrl.trim();
    if (!label) { this.toast.error('Label required', 'Give the pin a display name.'); return; }
    if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(rawKey)) {
      this.toast.error('Invalid key', 'Key must start with a letter/digit and contain only lowercase letters, digits, hyphens, underscores.');
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      this.toast.error('Invalid URL', 'URL must start with http:// or https://');
      return;
    }
    if (this.draftPins().find(p => p.key === rawKey)) {
      this.toast.error('Duplicate key', `A pin with key '${rawKey}' already exists.`);
      return;
    }
    this.draftPins.update(cur => [...cur, { key: rawKey, label, url }]);
    this.newLabel = ''; this.newKey = ''; this.newUrl = '';
  }

  removeAt(idx: number) {
    this.draftPins.update((cur) => cur.filter((_, i) => i !== idx));
  }

  moveUp(idx: number) {
    if (idx === 0) return;
    this.draftPins.update((cur) => {
      const next = [...cur];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  }

  moveDown(idx: number) {
    this.draftPins.update((cur) => {
      if (idx >= cur.length - 1) return cur;
      const next = [...cur];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }

  async save() {
    if (!this.dirty()) return;
    this.saving.set(true);
    try {
      const ok = await this.prefs.savePinnedScanners(this.draftPins());
      if (ok) {
        this.toast.success('Saved', 'Pinned scanners updated.');
        // Re-sync draft from the (server-returned) preferences.
        this._reset();
      }
    } finally {
      this.saving.set(false);
    }
  }

  revert() {
    if (!this.dirty()) return;
    if (!confirm('Revert unsaved changes back to the last-loaded values?')) return;
    this._reset();
  }

  onPinKeyEdit(idx: number, newKey: string) {
    // Enforced on save server-side; this keeps the field neat as the
    // user types so they see immediately if a keystroke isn't allowed.
    const cleaned = String(newKey || '').toLowerCase().replace(/[^a-z0-9_-]/g, '');
    this.draftPins.update((cur) => cur.map((p, i) => i === idx ? { ...p, key: cleaned } : p));
  }

  onPinLabelEdit(idx: number, newLabel: string) {
    this.draftPins.update((cur) => cur.map((p, i) => i === idx ? { ...p, label: newLabel } : p));
  }

  onPinUrlEdit(idx: number, newUrl: string) {
    this.draftPins.update((cur) => cur.map((p, i) => i === idx ? { ...p, url: newUrl } : p));
  }

  trackByIdx(idx: number, _p: PinnedScanner) { return idx; }

  private _reset() {
    // Deep-copy so edits to draft entries don't mutate the cached prefs.
    this.draftPins.set(this.prefs.pinnedScanners().map(p => ({ ...p })));
  }

  private _deriveKey(label: string): string {
    return label.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
  }
}
